import React from 'react';
import { motion } from 'framer-motion';
import ProjectStatsBadge from '../../../components/ProjectStatsBadge';
import ShareProjectButton from '../../../components/ShareProjectButton/ShareProjectButton';
import './ai-powered-communication-platform-for-deaf-and-mute.css';
import aiCommImg from '../../../assets/images/AI Powered Communication Platform.avif';

const AIPoweredCommunicationPlatformForDeafAndMute = () => {
  return (
    <motion.div 
      className="project-detail-container"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
    >
      <motion.h1 
        className="project-heading"
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.2 }}
      >
        AI Powered <span>Communication Platform</span> for Deaf and Mute
      </motion.h1>

      <motion.p 
        className="last-updated"
        initial={{ x: 50, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.4 }}
      ><br />
        Last Updated On: 30th May 2025
      </motion.p>

      <div className="project-content">
        <motion.div 
          className="project-left"
          initial={{ x: -100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <img src={aiCommImg} alt="AI Communication Platform" className="project-image" />
          <h2 className="project-subtitle">AI Powered Communication Platform</h2>
          <span className="project-category">AI & ML</span>
          <p className="project-author">Author: Praveen Kumar B</p>

          <ProjectStatsBadge docId="aiCommunicationPlatform" trackView={true} />

          {/* Buttons */}
          <div className="project-buttons">
            <a href="/under-construction" className="visit-project-button">
              → Visit the Project
            </a>
            <a href="https://github.com/Praveenkumar-king/AI-Powered-Communication-Platform-For-Deaf-and-Mute" className="visit-project-button">
              → View on GitHub
            </a>
            <ShareProjectButton
              project={{
                id: "aiCommunicationPlatform",
                title: "AI Powered Communication Platform for Deaf and Mute",
                path: "/projects/projectdetail/ai-powered-communication-platform-for-deaf-and-mute"
              }}
            />
          </div>
        </motion.div>

        <motion.div 
          className="project-right"
          initial={{ x: 100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          <h3 className="section-title">Project Details</h3>
          <p className="overview-text">
            Hi Friends & Family 🎉🎉, Here's The Detailed Explanation Of The Project.
          </p>

          <h3 className="section-title">Overview ✨</h3>
          <p className="overview-description">
            The <strong>AI Powered Communication Platform for Deaf and Mute</strong> is an innovative AI-driven system designed to bridge the communication gap for hearing and speech-impaired individuals. It leverages cutting-edge technologies to enable seamless interaction between users through sign, text, and speech. 🤖🧏‍♀️🗣️
          </p>

          <h3 className="section-title">Key Features 🛠️</h3>
          <ul className="feature-list">
            <li>Sign-to-Text Conversion ✋➡️📝 - AI model that detects hand gestures and translates them into text in real time.</li>
            <li>Speech-to-Text Conversion 🎤➡️📄 - Converts spoken words into readable text using Web Speech API and NLTK.</li>
            <li>User Authentication 🔐 - Includes login and sign-up pages to personalize and secure user experience.</li>
            <li>Trained Models 📚 - Over 100+ sign gestures trained using labeled image datasets.</li>
            <li>Text-to-Sign Animation 🎞️ - Avatar displays dynamic sign animations based on recognized text.</li>
            <li>Smart Dashboard 📊 - Interactive dashboard for live communication and feature access.</li>
            <li>Responsive Design 📱💻 - Available as both a web application and mobile-friendly app.</li>
            <li>Dataset Integration 🧾 - Preprocessed ASL image data with augmentation for training accuracy.</li>
            <li>Real-time Feedback 🔁 - Instant updates during conversations for smoother flow.</li>
          </ul>

          <h3 className="section-title">Benefits 🌈</h3>
          <ul className="benefits-list">
            <li><strong>Empowers Communication</strong> 🧏‍♂️🧏‍♀️ - Helps deaf and mute users express and receive messages clearly.</li>
            <li><strong>AI Assistance</strong> 🤖 - Smart recognition boosts interaction speed and comprehension.</li>
            <li><strong>Time-Saving</strong> ⏳ - No need for third-party interpreters or manual tools.</li>
            <li><strong>Inclusive Technology</strong> 🌍 - Promotes accessibility and equality through innovation.</li>
            <li><strong>Mobility</strong> 📲 - Accessible anytime, anywhere, on any device.</li>
          </ul>

          <h3 className="section-title">How It Works 🔍</h3>
          <ul className="how-it-works-list">
            <li>Sign Detection Model 🤟 - Uses rule-based mapping and computer vision to identify hand signs.</li>
            <li>Speech-to-Text Engine 🗣️➡️📝 - Live mic input is converted into text using WebKitSpeechRecognition.</li>
            <li>Keyword Extraction 🧠 - NLP (NLTK) filters meaningful terms from speech for clarity.</li>
            <li>Text-to-Avatar Gesture 🎭 - Recognized text triggers avatar to display equivalent signs.</li>
            <li>User Dashboard 📱 - Central hub for interaction and control.</li>
          </ul>

          <h3 className="section-title">Why Choose This System? 🤔</h3>
          <ul className="how-it-works-list">
            <li>User-Centric Design 👨‍💻 - Intuitive UI/UX for all age groups and accessibility needs.</li>
            <li>Cross-Platform Support 🌐 - Works seamlessly on both website and mobile app.</li>
            <li>Cloud Powered 🔋 - Firebase manages backend and real-time updates securely.</li>
            <li>Future Ready 💡 - Scalable to support new gestures, languages, and communication styles.</li>
            <li>Built with Latest Tools 🛠️ - Python, JavaScript, Next.js, and Firebase integration.</li>
          </ul>

          <h3 className="section-title">Conclusion 🎯</h3>
          <p>
            The <strong>AI Powered Communication Platform</strong> breaks barriers and opens doors for inclusive communication. 💬 Its intelligent systems, real-time conversions, and user-friendly design make it a revolutionary tool for supporting the deaf and mute community. 🌟
          </p>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default AIPoweredCommunicationPlatformForDeafAndMute;
